import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import '../styles/LandingPage.css';
import PromoVideo from './PromoVideo';
import { DottedSurface } from './ui/DottedSurface';
import { AnimatedHowItWorks, AnimatedConversation } from './remotion/AnimatedSections';
import PublicNav from './PublicNav';

const Spline = lazy(() => import('@splinetool/react-spline'));

/* ─── Data ─── */
const FEATURES = [
  { icon: '📄', title: 'PDF Download & Parsing', desc: 'AI downloads brochures attached to emails, extracts specs directly from PDFs, and links them to your sheet.' },
  { icon: '🔗', title: 'Link & Flyer Extraction', desc: 'Automatically captures listing links, uploads to Google Drive, and populates your Flyer column.' },
  { icon: '🔄', title: 'Smart Follow-ups', desc: 'No response? AI sends polite follow-ups on your schedule. Stops automatically when broker replies.' },
  { icon: '🚫', title: 'Unavailable Detection', desc: 'When a property is leased, AI moves it to Non-Viable and asks for alternatives automatically.' },
  { icon: '📅', title: 'Tour & Call Handling', desc: 'Broker offers a tour or call? AI pauses and notifies you with a suggested response to approve.' },
  { icon: '📊', title: 'Google Sheets Sync', desc: 'Every extraction writes directly to your Google Sheet in real-time. Your data is always up-to-date.' },
];

const CAMPAIGNS = [
  { name: 'Scrub Augusta', count: '47 props' },
  { name: 'Scrub Phoenix', count: '89 props' },
  { name: 'Scrub Dallas', count: '156 props' },
  { name: 'Scrub Denver', count: '73 props' },
];

const INTEGRATIONS = [
  {
    name: 'Microsoft Outlook',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20">
        <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.152-.354.228-.588.228h-8.174v-6.5l1.5 1.313 1.5-1.313V7.387z"/>
        <path fill="#0078D4" d="M24 5.5v1.887h-9v4.782l-1.5 1.313-1.5-1.313V5.5h12z"/>
        <path fill="#0A78D4" d="M15 18.669v-6.5l-1.5 1.313L12 12.169v6.5h-1.826c-.234 0-.43-.076-.588-.228a.785.785 0 01-.238-.576V7.387L0 4.5v15c0 .23.08.424.238.576.158.152.354.228.588.228H15z"/>
        <path fill="#28A8EA" d="M9.348 6.826v12.843H.826A.785.785 0 01.238 19.441.785.785 0 010 18.865V5.5l9.348 1.326z"/>
        <path fill="#FFFFFF" d="M6.5 9.5c-1.38 0-2.5 1.12-2.5 2.5s1.12 2.5 2.5 2.5S9 13.38 9 12s-1.12-2.5-2.5-2.5zm0 4c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
      </svg>
    )
  },
  {
    name: 'Google Sheets',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20">
        <path fill="#0F9D58" d="M14.5 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V7.5L14.5 2z"/>
        <path fill="#87CEAC" d="M14.5 2v5.5H20L14.5 2z"/>
        <path fill="#FFFFFF" d="M7 12h10v1H7zm0 2h10v1H7zm0 2h7v1H7zm0-6h10v1H7z"/>
      </svg>
    )
  },
  {
    name: 'Google Drive',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20">
        <path fill="#4285F4" d="M22.5 15.5l-3.5-6h-7l3.5 6z"/>
        <path fill="#FBBC05" d="M8 9.5L4.5 15.5l1.75 3h14l1.75-3H15.5l-3.5-6z"/>
        <path fill="#34A853" d="M8 9.5h7l-3.5-6h-7z"/>
      </svg>
    )
  },
  {
    name: 'OpenAI',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
        <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
      </svg>
    )
  },
];

/* ─── Main Landing Page ─── */

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const container = document.querySelector('.landing-page');
    if (!container) return;

    const handleScroll = () => {
      setScrolled(container.scrollTop > 40);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-page">
      <PublicNav scrolled={scrolled} />

      {/* ─── Hero Section ─── */}
      <section className="landing-hero" id="hero">
        <div className="landing-hero-bg">
          <div className="landing-hero-orb landing-hero-orb-1"></div>
          <div className="landing-hero-orb landing-hero-orb-2"></div>
          <div className="landing-hero-orb landing-hero-orb-3"></div>
        </div>
        <div className="landing-hero-split">
          <div className="landing-hero-content">
            <h1>
              AI That <span>Simultaneously</span> Handles All
              <br />
              Your Broker Outreach
            </h1>
            <p>
              Upload your scrub. AI emails every broker, reads their replies,
              extracts property data, downloads PDFs, and fills your sheet — all automatically.
            </p>
            <div className="landing-hero-actions">
              <Link to="/login" className="landing-cta-btn">
                Start Setup
              </Link>
              <button className="landing-cta-btn-secondary" onClick={() => scrollToSection('how-it-works')}>
                See How It Works
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
            </div>
            <div className="landing-hero-note">Set up in 5 minutes</div>
          </div>
          <div className="landing-hero-robot">
            <Suspense fallback={<div className="landing-hero-robot-loading" />}>
              <Spline scene="https://prod.spline.design/kZDDjO5HuC9GJUM2/scene.splinecode" />
            </Suspense>
          </div>
        </div>
        <div className="landing-hero-integrations">
          <span className="landing-proof-int-label">Integrates with</span>
          <div className="landing-proof-int-logos">
            {INTEGRATIONS.map((int_, i) => (
              <div key={i} className="landing-proof-int-item">
                <span className="landing-proof-int-icon">{int_.icon}</span>
                <span className="landing-proof-int-name">{int_.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Promo Video ─── */}
      <section className="landing-video-section">
        <PromoVideo />
      </section>


      {/* ─── Flow / How It Works Section ─── */}
      <section className="landing-flow-section" id="how-it-works">
        <div className="landing-section-badge">How It Works</div>
        <h2>Upload Your CoStar Export</h2>
        <p className="landing-flow-headline">AI emails every broker and fills out your sheet automatically.</p>

        <div className="landing-flow-visual">
          <AnimatedHowItWorks />
        </div>
      </section>

      {/* ─── Scale Section ─── */}
      <section className="landing-scale-section">
        <h2>Scales With You — No Limits</h2>
        <p>Handle as many properties and campaigns as you need</p>
        <div className="landing-scale-grid">
          <div className="landing-scale-card">
            <h3><span>Unlimited</span> Properties Per Sheet</h3>
            <div className="landing-scale-card-sub">Every row = an email thread handled by AI</div>
            <div className="landing-rows-bars">
              <div className="landing-row-bar" style={{ '--p': '100%' }} data-lbl="47 properties"></div>
              <div className="landing-row-bar" style={{ '--p': '75%' }} data-lbl="124 properties"></div>
              <div className="landing-row-bar" style={{ '--p': '50%' }} data-lbl="300+ properties"></div>
            </div>
          </div>
          <div className="landing-scale-card">
            <h3><span>Multiple</span> Campaigns at Once</h3>
            <div className="landing-scale-card-sub">Run different scrubs for different clients</div>
            <div className="landing-sheets-stack">
              {CAMPAIGNS.map((c, i) => (
                <div key={i} className="landing-mini-sheet">
                  <div className="n">{c.name}</div>
                  <div className="c">{c.count}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Features Section ─── */}
      <section className="landing-features-section" id="features">
        <div className="landing-section-badge">Features</div>
        <h2>More Than Just Email</h2>
        <p>Every feature you need to automate broker outreach completely</p>
        <div className="landing-features-grid">
          {FEATURES.map((f, i) => (
            <div key={i} className="landing-feature-card">
              <div className="landing-feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Email Example Section ─── */}
      <section className="landing-example-section">
        <div className="landing-section-badge">Live Example</div>
        <h2>Watch AI Handle a Real Conversation</h2>

        <div className="landing-animated-conversation">
          <AnimatedConversation />
        </div>
      </section>



      {/* ─── Footer with CTA ─── */}
      <footer className="relative overflow-hidden" style={{ background: 'transparent', minHeight: '77vh' }}>
        {/* Animated dots - contained within footer */}
        <DottedSurface theme="dark" />

        {/* Content */}
        <div className="relative z-10 text-center pt-24 pb-8">
          <div className="max-w-xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to <span style={{ background: 'linear-gradient(135deg, #3b82f6, #22d3ee)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Automate</span> Your Broker Outreach?
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              Join brokers who are saving hours every week with AI-powered email automation.
            </p>
            <Link to="/login" className="landing-cta-btn">
              Start Setup
            </Link>
            <div className="text-gray-500 text-sm mt-6">
              Works with Outlook + Google Sheets
            </div>
          </div>
        </div>

        {/* Copyright - positioned at bottom */}
        <div className="absolute bottom-8 left-0 right-0 text-center text-gray-500 text-sm z-10">
          <p>&copy; {new Date().getFullYear()} SiteSift.AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
